import { type FC, useMemo } from 'react';
import { usePipelineNodes } from '../hooks/usePipelineNodes';
import { usePipelineNodeRelationships } from '../hooks/usePipelineNodeRelationships';
import type { MeasureJoinTreeNode } from '../services/measure.service';
import { cn } from '@/lib/utils';
import { Table2, ArrowRight, GitBranch } from 'lucide-react';

interface JoinTreeLabelProps {
  joinTree: MeasureJoinTreeNode;
  highlightNodeIds?: string[];
}

interface TreeNodeLabelProps {
  node: MeasureJoinTreeNode;
  highlightNodeIds?: string[];
  depth: number;
  nodesById: Map<string, { id: string; label?: string; name?: string }>;
  relationshipsById: Map<string, { id: string; name: string }>;
}

const TreeNodeLabel: FC<TreeNodeLabelProps> = ({
  node,
  highlightNodeIds,
  depth,
  nodesById,
  relationshipsById,
}) => {
  const pipelineNode = nodesById.get(node.node_id);
  const relationship = node.relationship_id
    ? relationshipsById.get(node.relationship_id)
    : null;
  const isHighlighted = highlightNodeIds?.includes(node.node_id);

  const nodeName = pipelineNode?.label || pipelineNode?.name || node.node_id;
  const relationshipName = relationship?.name || 'Unnamed';
  const hasBranches = node.downstream && node.downstream.length > 1;

  return (
    <div>
      <div className="flex items-center gap-1" style={{ paddingLeft: depth * 16 }}>
        {depth > 0 && (
          <ArrowRight className="h-3 w-3 text-muted-foreground flex-shrink-0" />
        )}
        {relationship && (
          <span className="text-muted-foreground text-xs whitespace-nowrap">
            via <span className="font-medium">{relationshipName}</span> to
          </span>
        )}
        <span
          className={cn(
            'inline-flex items-center gap-1 font-medium whitespace-nowrap',
            isHighlighted ? 'text-primary' : 'text-foreground'
          )}
        >
          <Table2 className="h-3 w-3 flex-shrink-0" />
          {nodeName}
        </span>
        {hasBranches && (
          <GitBranch className="h-3 w-3 text-muted-foreground flex-shrink-0 ml-1" />
        )}
      </div>
      {/* Recursively render all downstream branches */}
      {node.downstream?.map((child, idx) => (
        <TreeNodeLabel
          key={`${child.node_id}-${idx}`}
          node={child}
          highlightNodeIds={highlightNodeIds}
          depth={depth + 1}
          nodesById={nodesById}
          relationshipsById={relationshipsById}
        />
      ))}
    </div>
  );
};

export const JoinTreeLabel: FC<JoinTreeLabelProps> = ({
  joinTree,
  highlightNodeIds,
}) => {
  const { data: nodesData } = usePipelineNodes({ page: 1, pageSize: 1000 });
  const { data: relationships } = usePipelineNodeRelationships();

  const nodesById = useMemo(() => {
    if (!nodesData?.records) return new Map();
    return new Map(nodesData.records.map((n) => [n.id, n]));
  }, [nodesData]);

  const relationshipsById = useMemo(() => {
    if (!relationships) return new Map();
    return new Map(relationships.map((r) => [r.id, r]));
  }, [relationships]);

  return (
    <div className="text-sm space-y-0.5">
      <TreeNodeLabel
        node={joinTree}
        highlightNodeIds={highlightNodeIds}
        depth={0}
        nodesById={nodesById}
        relationshipsById={relationshipsById}
      />
    </div>
  );
};
